import { withErrorBoundary } from 'app/components/error-boundary';
import { OnboardingLayout } from 'app/components/onboarding/layout';
import { Column } from 'app/design/primitives/box';
import { RemoteError } from 'app/design/primitives/remote-error';
import { useNavigationOptions } from 'app/hooks/navigation';
import { useScopedTranslation } from 'app/hooks/i18n';
import { CredentialsFormFields, useOnboardingNavigation } from 'app/screens/onboarding/credentials.shared';
import { useCredentialsActivationFlags } from 'app/screens/onboarding/hooks/use-credentials-activation-flags';
import { useCredentialsForm } from 'app/screens/onboarding/hooks/use-credentials-form';
import { useCredentialsSignupFlow } from 'app/screens/onboarding/hooks/use-credentials-signup-flow';
import type { NavigationScreen } from 'app/types/navigation';
import { usePageTitle } from 'app/utils/web-helpers';
import React, { useEffect, useRef } from 'react';
import type { TextInput } from 'react-native';
import { useTranslation } from 'react-i18next';

const CredentialsScreenComponent: NavigationScreen<'credentials'> = ({ navigation }) => {
  const { t } = useScopedTranslation();
  const { t: tAPI } = useTranslation('api-errors');

  const setPageTitle = usePageTitle('credentials');
  useEffect(() => setPageTitle('title'), [setPageTitle]);

  const { possibleActivationCode, requireActivationCode, userType } = useCredentialsActivationFlags();
  const { control, formState, getValues, handleSubmit } = useCredentialsForm(requireActivationCode);
  const signupFlow = useCredentialsSignupFlow({
    getValues,
    handleSubmit,
    requireActivationCode,
  });

  const { progressActiveColor } = useOnboardingNavigation(userType, { headerPresentation: 'solid' });

  useNavigationOptions(navigation, { headerBackVisible: false });

  const refPassword = useRef<TextInput>(null);
  const refPassword2 = useRef<TextInput>(null);
  const refInviteCode = useRef<TextInput>(null);

  const primaryProps = {
    disabled: !formState.isValid && !signupFlow.isLoggedIn,
    loading: signupFlow.isLoading || signupFlow.isInviteCodeLoading,
    onPress: () => {
      if (signupFlow.isLoggedIn) {
        signupFlow.onSubmitInviteCode();
      } else {
        void signupFlow.onSubmit();
      }
    },
  };

  const formFieldsProps = {
    control,
    inviteCodeFirst: signupFlow.inviteCodeFirst,
    isLoading: signupFlow.isLoading,
    isLoggedIn: signupFlow.isLoggedIn,
    possibleActivationCode,
    refInviteCode,
    refPassword,
    refPassword2,
    requireActivationCode,
    t,
  };

  const errorsBlock = (
    <>
      {signupFlow.error ? (
        <RemoteError {...{ error: signupFlow.error, message: tAPI(signupFlow.error.message) }} />
      ) : null}
      {signupFlow.createOnePlanError ? (
        <RemoteError
          {...{ error: signupFlow.createOnePlanError, message: tAPI(signupFlow.createOnePlanError.message) }}
        />
      ) : null}
    </>
  );

  return (
    <OnboardingLayout activeColor={progressActiveColor} primary={primaryProps}>
      <Column gap="spacing.general.x4">
        <CredentialsFormFields {...formFieldsProps} />
        {errorsBlock}
      </Column>
    </OnboardingLayout>
  );
};

export const CredentialsScreen: NavigationScreen<'credentials'> = withErrorBoundary(CredentialsScreenComponent);
